import { Link } from "react-router-dom";
import { useGetCategories } from "../../backend/hooks";
import WhatsAppButton from '../../common/whatsapp';
import { Skeleton } from '../../common/skeleton';

export default function BrowseCategories() {
  const { data, isLoading, error } = useGetCategories();

  const categories = data?.data || [];

  // Skeleton card – no link, just placeholder
  const SkeletonCard = () => (
    <div className="bg-surface-container-lowest p-2 rounded-xl flex flex-col">
      <Skeleton className="aspect-square w-full rounded-lg mb-4" />
      <div className="px-4 pb-6 space-y-3">
        <Skeleton className="h-6 rounded w-3/4" />
        <Skeleton className="h-4 rounded w-full" />
        <Skeleton className="h-4 rounded w-5/6" />
        <Skeleton className="h-4 rounded w-1/3" />
      </div>
    </div>
  );

  if (error) {
    return (
      <main className="pt-24 pb-20 px-6 max-w-7xl mx-auto text-center">
        <div className="text-lg text-error">Error: {error.message}</div>
      </main>
    );
  }

  return (
    <main className="pt-24">
      <WhatsAppButton />
      {/* Hero Section */}
      <section className="w-full bg-[#15415c] py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">
            Browse Our Categories
          </h1>
          <p className="mt-3 text-lg text-white/80 max-w-2xl mx-auto">
            Discover a wide range of physiotherapy equipment, occupational therapy products, sensory integration tools, and home healthcare solutions.
          </p>
        </div>
      </section>

      {/* Categories Grid */}
      <div className="pt-12 pb-24 px-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          {isLoading ? (
            <Skeleton className="h-4 w-40 rounded" />
          ) : (
            <p className="text-sm text-gray-500">
              {`${categories.length} ${categories.length === 1 ? "category" : "categories"} available`}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading
            ? Array.from({ length: 6 }).map((_, idx) => <SkeletonCard key={idx} />)
            : categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/ProductList/${category.id}`}
                  className="bg-surface-container-lowest p-2 rounded-xl flex flex-col group transition-all hover:shadow-md cursor-pointer"
                >
                  <div className="aspect-square w-full rounded-lg overflow-hidden mb-4 relative">
                    <img
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      src={process.env.REACT_APP_BE_URL + category.image}
                    />
                    <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors"></div>
                  </div>
                  <div className="px-4 pb-6">
                    <h3 className="text-xl font-headline font-bold text-primary mb-2">{category.name}</h3>
                    <p className="text-on-surface-variant text-sm mb-4 leading-relaxed line-clamp-3">
                      {category.description}
                    </p>
                    {/* Decorative arrow – no longer a separate link */}
                    <div className="inline-flex items-center gap-2 text-secondary font-semibold text-sm">
                      Explore Category
                      <span className="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform">
                        arrow_forward
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
        </div>

        {!isLoading && categories.length === 0 && (
          <div className="text-center py-12 text-on-surface-variant">No categories found.</div>
        )}
      </div>
    </main>
  );
}